import React, { useEffect, useRef, useState, useCallback } from "react";
import * as d3 from "d3";
import { FileEdit, RefreshCw, ZoomIn, ZoomOut } from "lucide-react";
import {
  getApplicationInsights,
  recalculateApplicationInsightStats,
  reanalyzeApplicationInsightPatterns,
} from "./services/api";

const INTERVIEW_STAGES = new Set([
  "phoneScreen",
  "phoneRejected",
  "technical",
  "techRejected",
  "onsite",
  "onsiteRejected",
  "offers",
  "accepted",
]);

const cardStyle = {
  background: "white",
  borderRadius: "16px",
  padding: "0.5rem",
  boxShadow: "0 2px 8px rgba(79,70,229,0.08)",
  flex: "auto",
};

const sectionTitleStyle = {
  fontSize: "1rem",
  fontWeight: 700,
  color: "#111827",
  marginBottom: "1rem",
};

const HomeScreen = ({
  navigateTo,
  jobList,
  userEmail,
  subscriptionCounts,
  applicationHistory,
  isLoadingApplicationHistory = false,
  loadSavedJobs,
  userStats,
  setJobDescription,
  handleErrors,
}) => {
  const svgRef = useRef(null);
  const zoomRef = useRef(null);
  const svgSelRef = useRef(null);
  const [diagramKey, setDiagramKey] = useState(0);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [isLoadingApplications, setIsLoadingApplications] = useState(false);
  const [insights, setInsights] = useState(null);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);
  const [isRecalculatingStats, setIsRecalculatingStats] = useState(false);
  const [isReanalyzingPatterns, setIsReanalyzingPatterns] = useState(false);
  const [insightsError, setInsightsError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setIsLoadingJobs(true);
    setIsLoadingApplications(true);
    loadSavedJobs().finally(() => {
      if (!cancelled) setIsLoadingJobs(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDiagramKey((prev) => prev + 1);
  }, [applicationHistory]);

  const loadInsights = useCallback(async () => {
    if (!userEmail) return;
    setIsLoadingInsights(true);
    setInsightsError("");
    try {
      const result = await getApplicationInsights(userEmail);
      setInsights(result);
    } catch (error) {
      setInsightsError("Could not load application insights right now.");
      await handleErrors?.("Load application insights", error);
    } finally {
      setIsLoadingInsights(false);
    }
  }, [userEmail, handleErrors]);

  useEffect(() => {
    loadInsights();
  }, [loadInsights]);

  const handleRecalculateStats = async () => {
    setIsRecalculatingStats(true);
    setInsightsError("");
    try {
      const result = await recalculateApplicationInsightStats(true, userEmail);
      setInsights(result);
    } catch (error) {
      setInsightsError(
        "Could not recalculate charts. Showing the latest cached insights.",
      );
      await handleErrors?.("Recalculate application insight charts", error);
    } finally {
      setIsRecalculatingStats(false);
    }
  };

  const handleReanalyzePatterns = async () => {
    setIsReanalyzingPatterns(true);
    setInsightsError("");
    try {
      const result = await reanalyzeApplicationInsightPatterns(true, userEmail);
      setInsights(result);
    } catch (error) {
      setInsightsError(
        "Could not analyze patterns. Showing the latest cached insights.",
      );
      await handleErrors?.("Analyze application insight patterns", error);
    } finally {
      setIsReanalyzingPatterns(false);
    }
  };

  useEffect(() => {
    if (applicationHistory && applicationHistory.length > 0) {
      generateSankeyDiagram(applicationHistory);
    } else if (svgRef.current) {
      d3.select(svgRef.current).selectAll("*").remove();
    }
    setIsLoadingApplications(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagramKey]);

  const handleZoomIn = useCallback(() => {
    if (svgSelRef.current && zoomRef.current)
      svgSelRef.current
        .transition()
        .duration(300)
        .call(zoomRef.current.scaleBy, 1.4);
  }, []);

  const handleZoomOut = useCallback(() => {
    if (svgSelRef.current && zoomRef.current)
      svgSelRef.current
        .transition()
        .duration(300)
        .call(zoomRef.current.scaleBy, 1 / 1.4);
  }, []);

  const generateSankeyDiagram = (history) => {
    if (!svgRef.current || !history || history.length === 0) return;

    const counts = history.reduce((acc, app) => {
      acc[app.result] = (acc[app.result] || 0) + 1;
      return acc;
    }, {});

    const applied = history.filter((app) => app.applied === true);
    const totalApps = history.length;

    const width = 900;
    const height = 400;
    const margin = {
      top: 30,
      right: width * 0.25,
      bottom: 30,
      left: width * 0.1,
    };

    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("width", "100%")
      .style("height", "100%")
      .style("cursor", "grab"); // Add grab cursor for better UX

    // Create the main group that will be transformed
    const g = svg.append("g");

    // Set up zoom behavior on the SVG
    const zoom = d3
      .zoom()
      .scaleExtent([0.4, 2.5])
      .on("zoom", (event) => {
        // Transform the group, not the SVG
        g.attr("transform", event.transform);
      });

    // Apply zoom to the SVG element
    svg.call(zoom);

    // Store references for the zoom buttons
    zoomRef.current = zoom;
    svgSelRef.current = svg;

    const rejected = counts.rejected || 0;
    const phoneRejected = counts.phoneRejected || 0;
    const techRejected = counts.techRejected || 0;
    const onsiteRejected = counts.onsiteRejected || 0;
    const total = applied.length || 0;

    const reachedPhoneScreen =
      (counts.phoneScreen || 0) +
      (counts.phoneRejected || 0) +
      (counts.technical || 0) +
      (counts.techRejected || 0) +
      (counts.onsite || 0) +
      (counts.onsiteRejected || 0) +
      (counts.offers || 0) +
      (counts.accepted || 0);

    const reachedTechnical =
      (counts.technical || 0) +
      (counts.techRejected || 0) +
      (counts.onsite || 0) +
      (counts.onsiteRejected || 0) +
      (counts.offers || 0) +
      (counts.accepted || 0);

    const reachedOnsite =
      (counts.onsite || 0) +
      (counts.onsiteRejected || 0) +
      (counts.offers || 0) +
      (counts.accepted || 0);

    const reachedOffers = (counts.offers || 0) + (counts.accepted || 0);
    const accepted = counts.accepted || 0;

    const allNodes = [
      { id: 0, name: `Applied\n${total}`, count: total, stage: "start" },
      {
        id: 1,
        name: `Rejected (Initial)\n${rejected}`,
        count: rejected,
        stage: "end",
      },
      {
        id: 2,
        name: `Phone Screen\n${reachedPhoneScreen}`,
        count: reachedPhoneScreen,
        stage: 1,
      },
      {
        id: 3,
        name: `Rejected (Phone)\n${phoneRejected}`,
        count: phoneRejected,
        stage: "end",
      },
      {
        id: 4,
        name: `Technical\n${reachedTechnical}`,
        count: reachedTechnical,
        stage: 2,
      },
      {
        id: 5,
        name: `Rejected (Tech)\n${techRejected}`,
        count: techRejected,
        stage: "end",
      },
      {
        id: 6,
        name: `Onsite\n${reachedOnsite}`,
        count: reachedOnsite,
        stage: 3,
      },
      {
        id: 7,
        name: `Rejected (Onsite)\n${onsiteRejected}`,
        count: onsiteRejected,
        stage: "end",
      },
      {
        id: 8,
        name: `Got offers\n${reachedOffers}`,
        count: reachedOffers,
        stage: 3,
      },
      { id: 9, name: `Accepted\n${accepted}`, count: accepted, stage: "end" },
    ];

    const nodes = allNodes.filter((node) => node.count > 0);
    if (nodes.length === 0) return;

    const nodeIdMap = new Map(nodes.map((node, idx) => [node.id, idx]));

    const allLinks = [
      { source: 0, target: 1, value: rejected },
      { source: 0, target: 2, value: reachedPhoneScreen },
      { source: 2, target: 3, value: phoneRejected },
      { source: 2, target: 4, value: reachedTechnical },
      { source: 4, target: 5, value: techRejected },
      { source: 4, target: 6, value: reachedOnsite },
      { source: 6, target: 7, value: onsiteRejected },
      { source: 6, target: 8, value: reachedOffers },
      { source: 8, target: 9, value: accepted },
    ];

    const links = allLinks
      .filter(
        (link) =>
          link.value > 0 &&
          nodeIdMap.has(link.source) &&
          nodeIdMap.has(link.target),
      )
      .map((link) => ({
        ...link,
        source: nodeIdMap.get(link.source),
        target: nodeIdMap.get(link.target),
      }));

    const nodeWidth = 20;
    const nodePadding = 40;
    const stageWidth = (width - margin.left - margin.right) / 4;

    nodes.forEach((node) => {
      if (node.stage === "start") {
        node.x = margin.left;
      } else if (node.stage === "end") {
        node.x = width - margin.right;
      } else {
        node.x = margin.left + node.stage * stageWidth;
      }
    });

    const heightScale = d3
      .scaleLinear()
      .domain([0, totalApps])
      .range([0, height - margin.top - margin.bottom]);

    const startNode = nodes.find((n) => n.stage === "start");
    if (startNode) {
      startNode.y = margin.top + heightScale(totalApps) / 2;
      startNode.height = heightScale(totalApps) / 1.5;
    }

    const middleNodes = nodes.filter((n) => typeof n.stage === "number");
    middleNodes.forEach((node, idx) => {
      node.y = margin.top + 90 + idx * 90;
      node.height = heightScale(node.count) / 1.5;
    });

    const endNodes = nodes.filter((n) => n.stage === "end");
    let currentY = margin.top;
    endNodes.forEach((node) => {
      node.height = heightScale(node.count) / 1.5;
      node.y = currentY + node.height / 1.5;
      currentY += node.height + nodePadding;
    });

    const colorScale = d3
      .scaleOrdinal()
      .domain(["progress", "rejected", "success"])
      .range(["#402c7a", "#a72d2d", "#166534"]);

    const linkGroup = g.append("g");

    links.forEach((link) => {
      const source = nodes[link.source];
      const target = nodes[link.target];
      const linkHeight = heightScale(link.value) / 1.5;

      const isRejection = target.name.includes("Rejected");
      const isSuccess = target.name.includes("Accepted");
      const color = isSuccess
        ? colorScale("success")
        : isRejection
          ? colorScale("rejected")
          : colorScale("progress");

      const gradientId = `home-gradient-${link.source}-${link.target}`;
      const gradient = svg
        .append("defs")
        .append("linearGradient")
        .attr("id", gradientId)
        .attr("gradientUnits", "userSpaceOnUse")
        .attr("x1", source.x + nodeWidth)
        .attr("x2", target.x);

      gradient
        .append("stop")
        .attr("offset", "0%")
        .attr("stop-color", color)
        .attr("stop-opacity", 0.5);

      gradient
        .append("stop")
        .attr("offset", "100%")
        .attr("stop-color", color)
        .attr("stop-opacity", 0.3);

      const path = d3.path();
      const midX = (source.x + target.x) / 2;

      path.moveTo(source.x + nodeWidth, source.y - linkHeight / 2);
      path.bezierCurveTo(
        midX,
        source.y - linkHeight / 2,
        midX,
        target.y - linkHeight / 2,
        target.x,
        target.y - linkHeight / 2,
      );
      path.lineTo(target.x, target.y + linkHeight / 2);
      path.bezierCurveTo(
        midX,
        target.y + linkHeight / 2,
        midX,
        source.y + linkHeight / 2,
        source.x + nodeWidth,
        source.y + linkHeight / 2,
      );
      path.closePath();

      linkGroup
        .append("path")
        .attr("d", path.toString())
        .attr("fill", `url(#${gradientId})`)
        .attr("stroke", "none")
        .style("opacity", 0.6)
        .on("mouseover", function () {
          d3.select(this).style("opacity", 0.9);
        })
        .on("mouseout", function () {
          d3.select(this).style("opacity", 0.6);
        });
    });

    const nodeGroup = g.append("g");

    nodes.forEach((node) => {
      const isRejection = node.name.includes("Rejected");
      const isSuccess = node.name.includes("Accepted");
      const color = isSuccess
        ? colorScale("success")
        : isRejection
          ? colorScale("rejected")
          : colorScale("progress");

      nodeGroup
        .append("rect")
        .attr("x", node.x)
        .attr("y", node.y - node.height / 2)
        .attr("width", nodeWidth)
        .attr("height", node.height)
        .attr("fill", color)
        .attr("rx", 4);

      const labelX = node.stage === "end" ? node.x + nodeWidth + 9 : node.x - 9;
      const textAnchor = node.stage === "end" ? "start" : "end";

      const lines = node.name.split("\n");
      lines.forEach((line, i) => {
        nodeGroup
          .append("text")
          .attr("x", labelX)
          .attr("y", node.y + (i - 0.5) * 16)
          .attr("text-anchor", textAnchor)
          .attr("alignment-baseline", "middle")
          .attr("fill", "#1f2937")
          .attr("font-size", i === 1 ? "14px" : "12px")
          .attr("font-weight", i === 1 ? "bold" : "normal")
          .text(line);
      });
    });

    g.insert("rect", ":first-child")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", width)
      .attr("height", height)
      .attr("fill", "transparent")
      .attr("pointer-events", "visible");
  };

  // --- Stat values: prefer cached counts from the user doc, fall back to
  // computing from the in-memory arrays in case lists arrive first. ---
  const totalApplications =
    userStats?.total_applications ??
    (applicationHistory ? applicationHistory.length : 0);

  const interviews =
    userStats?.total_interviews ??
    (applicationHistory
      ? applicationHistory.filter((app) => INTERVIEW_STAGES.has(app.result))
          .length
      : 0);

  const activeJobsSaved =
    userStats?.total_jobs_saved ?? (jobList ? jobList.length : 0);

  // --- Common Patterns ---
  const calcInterviewRate = (apps) => {
    if (apps.length < 3) return null;
    const reached = apps.filter((a) => INTERVIEW_STAGES.has(a.result)).length;
    return Math.round((reached / apps.length) * 100);
  };

  const withCoverLetter = applicationHistory
    ? applicationHistory.filter((a) => a.cover_letter === true)
    : [];
  const withoutCoverLetter = applicationHistory
    ? applicationHistory.filter((a) => a.cover_letter === false)
    : [];

  const coverLetterRate = calcInterviewRate(withCoverLetter);
  const noCoverLetterRate = calcInterviewRate(withoutCoverLetter);

  // --- Recommendations ---
  const recommendations = [];

  if (
    subscriptionCounts?.cover_letters_generated < 3 ||
    (coverLetterRate !== null &&
      noCoverLetterRate !== null &&
      coverLetterRate < noCoverLetterRate)
  ) {
    if (subscriptionCounts?.tier !== "free_trial") {
      recommendations.push({
        id: "coverLetter",
        text: "Try using cover letters in more applications - they may boost your interview rate!",
        actionLabel: "Try Now with a tailor",
        action: () => navigateTo("tailor"),
      });
    } else {
      recommendations.push({
        id: "coverLetterUpgrade",
        text: "Unlock cover letter generation to boost your interview chances",
        actionLabel: "Upgrade Now",
        action: () => navigateTo("subscription"),
      });
    }
  }

  if (
    !applicationHistory ||
    applicationHistory.filter((a) => a.referred).length < 2
  ) {
    recommendations.push({
      id: "referral",
      text: "Try getting a referral - referred applicants often skip the initial screen",
      actionLabel: "Join our Referral Waitlist",
      action: () => navigateTo("referral"),
    });
  }

  if (!applicationHistory || applicationHistory.length < 5) {
    recommendations.push({
      id: "applyMore",
      text: "Apply to more roles to unlock deeper insights",
      actionLabel: null,
      action: null,
    });
  }

  const insightPayload = insights?.insights;
  const binaryFeatures = insightPayload?.binary_features || [];
  const timeGraph = insightPayload?.time_graph || {};
  const patternPayload = insightPayload?.patterns || {};
  const hasApplicationHistory =
    Array.isArray(applicationHistory) && applicationHistory.length > 0;
  const hasCachedInsights = Boolean(insights?.cached && insightPayload);
  const maxBinaryCallbacks = Math.max(
    1,
    ...binaryFeatures.flatMap((feature) => [
      feature.with_callback_count || 0,
      feature.without_callback_count || 0,
    ]),
  );
  const maxTimeCallbacks = Math.max(1, timeGraph.max_callback_count || 0);

  const patternGroups = [
    {
      title: "Common callback job titles",
      items: patternPayload.common_job_titles || [],
    },
    {
      title: "Common skill matches",
      items: patternPayload.common_skill_matches || [],
    },
    // {
    //   title: "Resume patterns in callbacks",
    //   items: patternPayload.common_resume_terms || [],
    // },
  ];

  return (
    <div className="min-h-screen bg-loading-color">
      <div
        className="max-w-6xl mx-auto px-4 py-8"
        style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
      >
        {/* A. Stats Row */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: "0.5rem",
            textAlign: "center",
          }}
        >
          {[
            { label: "Total Applications", value: totalApplications },
            { label: "Interviews", value: interviews },
            // { label: "Cover Letters Used", value: coverLettersUsed },
            { label: "Active Jobs Saved", value: activeJobsSaved },
          ].map((stat) => (
            <div key={stat.label} style={cardStyle}>
              <div
                style={{
                  fontSize: "2rem",
                  fontWeight: 800,
                  color: "#402c7a",
                  lineHeight: 1.1,
                }}
              >
                {stat.value}
              </div>
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "#6b7280",
                  marginTop: "0.25rem",
                }}
              >
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* B. Application Pipeline (Sankey) */}
        <div style={cardStyle} className="relative">
          <div style={sectionTitleStyle}>Application Pipeline</div>
          <div
            style={{
              position: "absolute",
              bottom: 10,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              background: "rgba(255,255,255,0.92)",
              border: "1px solid #e5e7eb",
              borderRadius: "10px",
              padding: "5px 8px",
              boxShadow: "0 1px 8px rgba(64,44,122,0.08)",
              zIndex: 10,
            }}
          >
            <button
              onClick={handleZoomIn}
              title="Zoom in"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 30,
                height: 30,
                border: "none",
                borderRadius: "6px",
                background: "transparent",
                cursor: "pointer",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#f3f0ff")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              <ZoomIn size={16} />
            </button>
            <div style={{ width: 1, height: 18, background: "#e5e7eb" }} />
            <button
              onClick={handleZoomOut}
              title="Zoom out"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 30,
                height: 30,
                border: "none",
                borderRadius: "6px",
                background: "transparent",
                cursor: "pointer",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#f3f0ff")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              <ZoomOut size={16} />
            </button>
          </div>
          {applicationHistory && applicationHistory.length > 0 ? (
            <svg ref={svgRef} style={{ width: "100%" }} />
          ) : (
            <div
              style={{
                textAlign: "center",
                color: "#9ca3af",
                padding: "2rem 0",
              }}
            >
              {isLoadingApplicationHistory || isLoadingApplications ? (
                <div>Loading...</div>
              ) : (
                <div>Add applications to see your pipeline</div>
              )}
            </div>
          )}
        </div>

        {/* C. Common Patterns */}
        <div style={cardStyle}>
          <div
            style={{
              ...sectionTitleStyle,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "0.75rem",
            }}
          >
            <span>What's Working</span>
            <div style={{ display: "flex", gap: "0.45rem", flexWrap: "wrap" }}>
              <button
                onClick={handleRecalculateStats}
                disabled={isRecalculatingStats}
                className="buttons-indigo"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.35rem",
                  padding: "0.45rem 0.7rem",
                  fontSize: "0.78rem",
                }}
              >
                <RefreshCw
                  size={14}
                  style={{
                    animation: isRecalculatingStats
                      ? "spin 1s linear infinite"
                      : "none",
                  }}
                />
                {isRecalculatingStats ? "Updating..." : "Recalculate charts"}
              </button>
              <button
                onClick={handleReanalyzePatterns}
                disabled={isReanalyzingPatterns}
                className="buttons-indigo"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.35rem",
                  padding: "0.45rem 0.7rem",
                  fontSize: "0.78rem",
                }}
              >
                <RefreshCw
                  size={14}
                  style={{
                    animation: isReanalyzingPatterns
                      ? "spin 1s linear infinite"
                      : "none",
                  }}
                />
                {isReanalyzingPatterns ? "Analyzing..." : "Analyze patterns"}
              </button>
            </div>
          </div>

          {isLoadingInsights || (isLoadingApplications && !insights) ? (
            <p style={{ fontSize: "0.85rem", color: "#6b7280" }}>
              Loading cached insights...
            </p>
          ) : insightsError ? (
            <p style={{ fontSize: "0.85rem", color: "#a72d2d" }}>
              {insightsError}
            </p>
          ) : !hasCachedInsights ? (
            <div style={{ display: "grid", gap: "0.75rem" }}>
              <p
                style={{
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  color: "#402c7a",
                }}
              >
                {hasApplicationHistory
                  ? "What's Working insights are ready to analyze your application history."
                  : "Add applications to unlock What's Working insights."}
              </p>
              {hasApplicationHistory && (
                <div
                  style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}
                >
                  <button
                    onClick={handleRecalculateStats}
                    disabled={isRecalculatingStats}
                    className="buttons-indigo"
                    style={{ justifySelf: "start" }}
                  >
                    {isRecalculatingStats ? "Updating..." : "Calculate charts"}
                  </button>
                  <button
                    onClick={handleReanalyzePatterns}
                    disabled={isReanalyzingPatterns}
                    className="buttons-indigo"
                    style={{ justifySelf: "start" }}
                  >
                    {isReanalyzingPatterns
                      ? "Analyzing..."
                      : "Analyze patterns"}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: "grid", gap: "1rem" }}>
              {insights?.stale && (
                <p style={{ fontSize: "0.78rem", color: "#92400e" }}>
                  Showing cached insights. Reanalyze to refresh them.
                </p>
              )}
              {insightPayload.status === "insufficient_data" &&
                insightPayload.message && (
                  <p
                    style={{
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      color: "#402c7a",
                    }}
                  >
                    {insightPayload.message}
                  </p>
                )}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                  gap: "1rem",
                }}
              >
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "14px",
                    padding: "0.9rem",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.86rem",
                      fontWeight: 700,
                      color: "#111827",
                      marginBottom: "0.2rem",
                    }}
                  >
                    Callback counts by application features
                  </div>
                  <div
                    style={{
                      fontSize: "0.72rem",
                      color: "#6b7280",
                      marginBottom: "0.9rem",
                    }}
                  >
                    Y-axis: applications that reached an interview callback
                    stage
                  </div>
                  {binaryFeatures.length ? (
                    <div style={{ display: "grid", gap: "0.9rem" }}>
                      {binaryFeatures.map((feature) => (
                        <div key={feature.key}>
                          <div
                            style={{
                              fontSize: "0.78rem",
                              fontWeight: 700,
                              color: "#374151",
                              marginBottom: "0.35rem",
                            }}
                          >
                            {feature.label}
                          </div>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: "0.6rem",
                              alignItems: "end",
                              height: "120px",
                            }}
                          >
                            {[
                              {
                                label: feature.with_label,
                                callbacks: feature.with_callback_count,
                                total: feature.with_application_count,
                                color: "#402c7a",
                              },
                              {
                                label: feature.without_label,
                                callbacks: feature.without_callback_count,
                                total: feature.without_application_count,
                                color: "#9b8ac8",
                              },
                            ].map((bar) => (
                              <div
                                key={bar.label}
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "end",
                                  height: "100%",
                                  gap: "0.25rem",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "0.78rem",
                                    fontWeight: 800,
                                    color: "#111827",
                                  }}
                                >
                                  {bar.callbacks || 0}
                                </span>
                                <div
                                  title={`${bar.callbacks || 0} callbacks from ${bar.total || 0} applications`}
                                  style={{
                                    width: "70%",
                                    minHeight: "4px",
                                    height: `${Math.max(
                                      4,
                                      ((bar.callbacks || 0) /
                                        maxBinaryCallbacks) *
                                        78,
                                    )}px`,
                                    borderRadius: "8px 8px 3px 3px",
                                    background: bar.color,
                                  }}
                                />
                                <span
                                  style={{
                                    minHeight: "30px",
                                    textAlign: "center",
                                    fontSize: "0.68rem",
                                    color: "#6b7280",
                                  }}
                                >
                                  {bar.label}
                                  <br />
                                  {bar.total || 0} apps
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontSize: "0.82rem", color: "#6b7280" }}>
                      Not enough binary feature data yet.
                    </p>
                  )}
                </div>

                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "14px",
                    padding: "0.9rem",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.86rem",
                      fontWeight: 700,
                      color: "#111827",
                      marginBottom: "0.2rem",
                    }}
                  >
                    Callback timing by week
                  </div>
                  <div
                    style={{
                      fontSize: "0.72rem",
                      color: "#6b7280",
                      marginBottom: "0.9rem",
                    }}
                  >
                    Darker cells mean more callback-producing applications
                  </div>
                  {timeGraph.points?.length ? (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: `70px repeat(${timeGraph.days.length}, minmax(24px, 1fr))`,
                        gap: "0.25rem",
                        alignItems: "center",
                      }}
                    >
                      <div />
                      {timeGraph.days.map((day) => (
                        <div
                          key={day}
                          style={{
                            fontSize: "0.68rem",
                            color: "#6b7280",
                            textAlign: "center",
                          }}
                        >
                          {day}
                        </div>
                      ))}
                      {timeGraph.time_buckets.map((bucket) => (
                        <React.Fragment key={bucket}>
                          <div
                            style={{ fontSize: "0.68rem", color: "#6b7280" }}
                          >
                            {bucket}
                          </div>
                          {timeGraph.days.map((day) => {
                            const point = timeGraph.points.find(
                              (p) => p.day === day && p.time_bucket === bucket,
                            );
                            const count = point?.callback_count || 0;
                            const opacity = count
                              ? 0.22 + (count / maxTimeCallbacks) * 0.78
                              : 0.08;
                            return (
                              <div
                                key={`${day}-${bucket}`}
                                title={`${day} ${bucket}: ${count} callbacks`}
                                style={{
                                  aspectRatio: "1.35 / 1",
                                  borderRadius: "7px",
                                  background: `rgba(64, 44, 122, ${opacity})`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: count ? "white" : "#9ca3af",
                                  fontSize: "0.7rem",
                                  fontWeight: 800,
                                }}
                              >
                                {count || ""}
                              </div>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontSize: "0.82rem", color: "#6b7280" }}>
                      Not enough timestamp data yet.
                    </p>
                  )}
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: "0.75rem",
                }}
              >
                {patternGroups.map((group) => (
                  <div
                    key={group.title}
                    style={{
                      background: "#f8f7fc",
                      borderRadius: "14px",
                      padding: "0.85rem",
                      border: "1px solid #ebe7f7",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.82rem",
                        fontWeight: 800,
                        color: "#402c7a",
                        marginBottom: "0.55rem",
                      }}
                    >
                      {group.title}
                    </div>
                    {group.items.length ? (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "0.4rem",
                        }}
                      >
                        {group.items.slice(0, 8).map((item, index) => {
                          const value =
                            typeof item === "string"
                              ? item
                              : item.value || item.text;
                          const count =
                            typeof item === "string" ? null : item.count;
                          return (
                            <span
                              key={`${group.title}-${value}-${index}`}
                              style={{
                                background: "white",
                                border: "1px solid #ded7f2",
                                borderRadius: "999px",
                                color: "#3730a3",
                                fontSize: "0.72rem",
                                fontWeight: 700,
                                padding: "0.28rem 0.52rem",
                              }}
                            >
                              {value}
                              {count ? ` · ${count}` : ""}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <p style={{ fontSize: "0.78rem", color: "#6b7280" }}>
                        Not enough callback text yet.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* D. Smart Recommendations */}
        {recommendations.length > 0 && (
          <div style={cardStyle}>
            <div style={sectionTitleStyle}>Recommendations</div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
              }}
            >
              {recommendations.map((rec) => (
                <div
                  key={rec.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: "#e9e2fea5",
                    borderRadius: "12px",
                    padding: "0.75rem 1rem",
                  }}
                >
                  <span style={{ fontSize: "0.9rem", color: "#3730a3" }}>
                    {rec.text}
                  </span>
                  {rec.actionLabel && (
                    <button onClick={rec.action} className="buttons-indigo">
                      {rec.actionLabel}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* E. Saved Jobs Preview */}
        <div style={cardStyle}>
          <div style={sectionTitleStyle}>Saved Jobs</div>
          {jobList && jobList.length > 0 ? (
            <>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5rem",
                }}
              >
                {jobList.slice(0, 5).map((job) => (
                  <div
                    key={job.id || job.job_id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "0.6rem 0.75rem",
                      background: "#f9fafb",
                      borderRadius: "10px",
                    }}
                    className="relative flex items-center justify-between"
                  >
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          color: "#111827",
                        }}
                      >
                        {job.title.split("(")[0].split(",")[0]}
                      </span>
                      <span
                        style={{
                          fontSize: "0.8rem",
                          color: "#6b7280",
                        }}
                      >
                        {job.company}
                      </span>
                    </div>

                    <button
                      onClick={() =>
                        navigateTo("tailor", false, null, {
                          jobDescription: job.description,
                        })
                      }
                      className="buttons-indigo absolute right"
                      style={{ marginRight: "0.25rem" }}
                    >
                      <FileEdit style={{ width: "14px", height: "14px" }} />{" "}
                      Tailor
                    </button>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: "0.75rem", textAlign: "right" }}>
                <button
                  onClick={() => navigateTo("yourJobs")}
                  style={{ background: "none" }}
                  className="text-sm text-indigo-600"
                >
                  View all jobs
                </button>
              </div>
            </>
          ) : isLoadingJobs ? (
            <div
              style={{
                textAlign: "center",
                padding: "1.5rem 0",
                color: "#9ca3af",
                fontSize: "0.875rem",
              }}
            >
              Loading saved jobs…
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "1.5rem 0" }}>
              <p style={{ color: "#9ca3af", marginBottom: "1rem" }}>
                Save a job to get started
              </p>
              <button
                onClick={() => navigateTo("yourJobs")}
                className="buttons-indigo"
              >
                Add a Job
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomeScreen;
